/**
 * 储蓄目标页（**I 域** · `/goals` · `DR-050` · 批 85）
 *
 * 依据：`I域PRD` **§5.1**–**§5.3** · `AC-I1.1`–`I1.8` · `AC-I2.1`–`I2.5` · `AC-I3.1`–`I3.7` ·
 * `AC-I4.3`（建议句式）· `AC-I4.5`（删目标）· 铁律 8（**红=正/流入**）· `DR-023`（端侧金额聚合 = 0）
 *
 * ## 🔴 本页**不算任何数**
 *
 * 进度百分比、还差多少、预计达成日、里程碑是否点亮、成员贡献合计 —— **全部是服务端下发字段**。
 * 端侧唯一"看起来像算术"的两处是字符串搬运（`percentTextOf` / `barPercentOf`），
 * 它们不做除法（`AC-P1.2` 在端侧封死了 `/`）。
 *
 * ## 🔴 三类颜色语义（别弄反）
 *
 * | 场景 | 颜色 | 依据 |
 * |---|---|---|
 * | 取出（钱流出目标） | **琥珀** `c-notice` | `AC-I2.4`：红色 = 正/流入，用红说"取出"语义反转 |
 * | 节奏偏慢 / 已过期 | **琥珀** `c-notice` | 同上；文案是建议句式（`AC-I4.3`） |
 * | 已达成 | `c-ok` | 达成是好事，走"成功"色 |
 *
 * ## 🔴 无排行榜（`AC-I3.4` 红线）
 *
 * 成员贡献**按服务端给的稳定序**（`userId` 升序）原样拼接 —— 本页**不按金额排序、不显示占比**。
 */

import { useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  GoalMove,
  formatMoneyText,
  type AccountView,
  type Cents,
  type CreateGoalRequest,
  type GoalContributionListResult,
  type GoalMoveRequest,
  type GoalView,
  type UpdateGoalRequest,
} from '@jiaying/shared';
import {
  barPercentOf,
  buildCreateGoalRequest,
  buildUpdateGoalRequest,
  celebrateLines,
  contributionsText,
  draftFromGoal,
  emptyGoalDraft,
  goalDeletePlan,
  goalDraftErrors,
  isEmptyUpdate,
  milestoneAriaLabel,
  milestoneText,
  paceToneOf,
  progressBadgeText,
  remainingText,
  type GoalDraft,
} from '../features/goal.js';
import { localToday } from '../app/local-day.js';
import { useApp } from '../app/store.js';
import {
  createGoal,
  deleteGoal,
  listAccounts,
  listGoalContributions,
  listGoals,
  listPaydayCandidates,
  moveGoal,
  updateGoal,
} from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { Amount } from '../ui/Amount.js';
import { AnchorBar } from '../ui/AnchorBar.js';
import { Grid } from '../ui/Grid.js';
import { Loading } from '../ui/Loading.js';
import { GoalForm } from '../ui/GoalForm.js';
import { GoalMoveDialog } from '../ui/GoalMoveDialog.js';

/** API 错误 → 可读消息（服务端 `message` 原样展示，不翻译 —— 同报销弹窗的处置） */
function apiMessages(e: unknown): readonly string[] {
  if (!(e instanceof ApiError)) return [String(e)];
  return [String((e.body as { message?: unknown }).message ?? '请求失败')];
}

export function GoalsPage(): ReactElement {
  const qc = useQueryClient();
  const { session } = useApp();
  const meId = session?.userId ?? '';
  const today = localToday();

  const goals = useQuery({ queryKey: ['goals'], queryFn: listGoals });
  // 🆕 F3 入口（`AC-F3.11` 常驻）：只有真有「未分配的工资」时才显示提示条
  const payday = useQuery({ queryKey: ['paydayCandidates'], queryFn: listPaydayCandidates });
  const navigate = useNavigate();
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  /** `null` = 表单关闭；`'create'` = 新建；其它 = 正在编辑该 id */
  const [editing, setEditing] = useState<'create' | string | null>(null);
  const [draft, setDraft] = useState<GoalDraft>(emptyGoalDraft());
  const [formError, setFormError] = useState<string | null>(null);
  const [moving, setMoving] = useState<{ goal: GoalView; move: GoalMove } | null>(null);
  const [contribId, setContribId] = useState<string | null>(null);
  /** 两步删除：第一次点击弹确认，第二次才真删 */
  const [pendingDelete, setPendingDelete] = useState<GoalView | null>(null);
  /** 🔴 吸顶页内导航的引用，必须指向**内容区的直接容器**（`AC-O2.36`） */
  const contentRef = useRef<HTMLDivElement | null>(null);

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['goals'] });
    // 攒/取会改账户余额 ⇒ 资产页 / 净资产 / 流水 / 提醒缓存一起失效（同批 83/84 口径）
    void qc.invalidateQueries({ queryKey: ['accounts'] });
    void qc.invalidateQueries({ queryKey: ['networth'] });
    void qc.invalidateQueries({ queryKey: ['transactions'] });
    void qc.invalidateQueries({ queryKey: ['reminders'] });
  };

  /**
   * 建 / 改共用一个 mutation —— 🔴 请求体由**调用方组装好**（`build*Request`），
   * mutation 只做"发哪一个"。不在这里按 `editing` 重新拼一遍：那会让
   * 「只发改过的键」这条规则出现在两个地方（铁律 24）。
   */
  const save = useMutation({
    mutationFn: (input: { id: string | null; create?: CreateGoalRequest; update?: UpdateGoalRequest }): Promise<GoalView> =>
      input.id === null
        ? createGoal(input.create as CreateGoalRequest)
        : updateGoal(input.id, input.update as UpdateGoalRequest),
    onSuccess: () => {
      setEditing(null);
      setFormError(null);
      refresh();
    },
    onError: (e: unknown) => setFormError(apiMessages(e).join('；')),
  });

  const move = useMutation({
    mutationFn: (input: { id: string; req: GoalMoveRequest }) => moveGoal(input.id, input.req),
    onSuccess: () => {
      setMoving(null);
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (input: { id: string; forfeit: boolean }) => deleteGoal(input.id, input.forfeit),
    onSuccess: () => {
      setPendingDelete(null);
      refresh();
    },
    onError: (e: unknown) => {
      setFormError(apiMessages(e).join('；'));
      setPendingDelete(null);
    },
  });

  const accountsList: readonly AccountView[] = accounts.data?.items ?? [];
  /** 只列**可见**账户（隐藏账户不出现在选择器里 —— 与「记一笔」同处置） */
  const pickable = useMemo(() => accountsList.filter((a) => !a.hidden), [accountsList]);

  if (goals.isPending || accounts.isPending) return <Loading />;
  if (goals.isError) return <Alert severity="error">目标读取失败：{String(goals.error)}</Alert>;

  const { items, currency, totalSavedCents, totalTargetCents, reachedCount } = goals.data;
  const busy = save.isPending || remove.isPending;

  /** 与目标同币种的账户（币种判定仍以服务端为准，这里只是少给几个必错的选项） */
  const sameCurrency = (goal: GoalView | null): readonly AccountView[] =>
    pickable.filter((a) => goal === null || a.currency === goal.currency);

  const openCreate = (): void => {
    setDraft(emptyGoalDraft());
    setFormError(null);
    setEditing('create');
  };

  const openEdit = (g: GoalView): void => {
    setDraft(draftFromGoal(g));
    setFormError(null);
    setEditing(g.id);
  };

  const submitForm = (): void => {
    if (editing === null) return;
    if (editing === 'create') {
      save.mutate({ id: null, create: buildCreateGoalRequest(draft) });
      return;
    }
    const current = items.find((g) => g.id === editing);
    if (current === undefined) {
      setEditing(null);
      return;
    }
    const req = buildUpdateGoalRequest(draft, current);
    if (isEmptyUpdate(req)) {
      // 🔴 一个字都没改 ⇒ 不发请求（也就不会误触发"换绑"这条硬规则）
      setEditing(null);
      return;
    }
    save.mutate({ id: editing, update: req });
  };

  const editingGoal =
    editing === null || editing === 'create' ? null : (items.find((g) => g.id === editing) ?? null);

  return (
    <Stack spacing={2} ref={contentRef}>
      {/* 🔴 吸顶页内导航 —— 必须是内容区第一个子节点 */}
      <AnchorBar contentRef={contentRef} />

      {/* 🆕 F3 发薪日提示（`AC-F3.1`：只提示，不自动启动） */}
      {(payday.data?.items.length ?? 0) > 0 && (
        <Alert
          severity="info"
          data-testid="goals-payday-hint"
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/paydays')}>
              去分配
            </Button>
          }
        >
          有 {payday.data?.items.length} 笔工资还没分配 —— 要现在把「必付 + 目标攒」安排一下吗？
        </Alert>
      )}

      {/* ① 概览 */}
      <Card data-anchor="总览">
        <CardContent>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                全部目标已攒（服务端算好下发 · 钱仍在各自账户里）
              </Typography>
              <Amount
                cents={totalSavedCents as Cents}
                currency={currency}
                colored={false}
                variant="h5"
                sx={{ fontWeight: 700, my: 0.5 }}
              />
              <Typography variant="body2" color="text.secondary">
                目标合计 {formatMoneyText(totalTargetCents as Cents, currency)} · 已达成{' '}
                {String(reachedCount)} / {String(items.length)} 个
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={openCreate}
              disabled={editing !== null}
              data-testid="goal-create"
              sx={{ flexShrink: 0 }}
            >
              新建目标
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* ② 表单（新建 / 编辑） */}
      {editing !== null && (
        <Card data-anchor="编辑目标">
          <CardContent>
            <GoalForm
              draft={draft}
              errors={goalDraftErrors(draft)}
              accounts={sameCurrency(editingGoal)}
              mode={editing === 'create' ? 'create' : 'edit'}
              accountLocked={editingGoal !== null && editingGoal.moveCount > 0}
              busy={busy}
              onChange={setDraft}
              onSubmit={submitForm}
              onCancel={() => {
                setEditing(null);
                setFormError(null);
              }}
            />
            {formError !== null && (
              <Alert severity="error" sx={{ mt: 1 }} data-testid="goal-form-error">
                {formError}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* ③ 目标卡 */}
      {items.length === 0 ? (
        <Alert severity="info">还没有储蓄目标。先建一个「应急金」试试 —— 它可以没有截止日。</Alert>
      ) : (
        <Grid cols={2} sx={{ alignItems: 'start' }}>
          {items.map((g) => (
            <GoalCard
              key={g.id}
              goal={g}
              meId={meId}
              disabled={editing !== null}
              onSave={() => setMoving({ goal: g, move: GoalMove.Save })}
              onWithdraw={() => setMoving({ goal: g, move: GoalMove.Withdraw })}
              onDetail={() => setContribId(g.id)}
              onEdit={() => openEdit(g)}
              onDelete={() => setPendingDelete(g)}
            />
          ))}
        </Grid>
      )}

      {/* ④ 攒 / 取 */}
      <GoalMoveDialog
        // 🔴 key：换目标 / 换动作时**必须重建**，否则草稿会带着上一个目标的内容
        key={`${moving?.goal.id ?? 'none'}:${moving?.move ?? 'none'}`}
        goal={moving?.goal ?? null}
        accounts={sameCurrency(moving?.goal ?? null)}
        today={today}
        initialMove={moving?.move ?? GoalMove.Save}
        onClose={() => setMoving(null)}
        onSubmit={async (req) => {
          if (moving === null) return;
          await move.mutateAsync({ id: moving.goal.id, req });
        }}
      />

      {/* ⑤ 贡献流抽屉 */}
      <GoalContributionsDrawer
        goalId={contribId}
        goal={items.find((g) => g.id === contribId) ?? null}
        meId={meId}
        onClose={() => setContribId(null)}
      />

      {/* ⑥ 删除确认（`AC-I4.5` 的"腿留着、归属会丢"必须先说清） */}
      <Dialog open={pendingDelete !== null} onClose={() => setPendingDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>删除「{pendingDelete?.name ?? ''}」</DialogTitle>
        <DialogContent>
          {pendingDelete !== null && (
            <Typography variant="body2" data-testid="goal-delete-warning">
              {goalDeletePlan(pendingDelete).warning}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} data-testid="goal-delete-cancel">
            取消
          </Button>
          <Button
            color="error"
            variant="contained"
            data-testid="goal-delete-confirm"
            onClick={() => {
              if (pendingDelete === null) return;
              remove.mutate({
                id: pendingDelete.id,
                // 🔴 有攒钱额 ⇒ 必须显式带 forfeit（服务端也会 409 兜底）
                forfeit: goalDeletePlan(pendingDelete).needsForfeit,
              });
            }}
          >
            确认删除
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ═══════════════════════════ 目标卡 ═══════════════════════════

interface GoalCardProps {
  goal: GoalView;
  meId: string;
  disabled: boolean;
  onSave: () => void;
  onWithdraw: () => void;
  onDetail: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function GoalCard({
  goal,
  meId,
  disabled,
  onSave,
  onWithdraw,
  onDetail,
  onEdit,
  onDelete,
}: GoalCardProps): ReactElement {
  const theme = useTheme();
  const tone = paceToneOf(goal.pace);
  // 🔴 令牌表是 `Record<string, string>`，索引可能返回 `undefined` ⇒ 末尾挂一个 MUI 主题色兜底，
  //    这样 `getContrastText(accent)` 拿到的恒是 `string`（不靠 `!` 断言掩盖）
  const brand = theme.jiaying.tokens['c-brand'] ?? theme.palette.primary.main;
  const accent = goal.progress.reached
    ? (theme.jiaying.tokens['c-ok'] ?? theme.palette.success.main)
    : (goal.color ?? brand);
  const hintColor =
    tone === 'notice'
      ? (theme.jiaying.tokens['c-notice'] ?? theme.palette.warning.main)
      : tone === 'ok'
        ? (theme.jiaying.tokens['c-ok'] ?? theme.palette.success.main)
        : theme.jiaying.text.tertiary;

  return (
    <Card data-testid={`goal-card-${goal.id}`} sx={{ borderTop: `3px solid ${accent}` }}>
      <CardContent>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                {goal.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {goal.dueDate === null ? '无截止日' : `截止 ${goal.dueDate}`}
                {goal.accountName === null ? ' · 尚未关联账户' : ` · 关联账户：${goal.accountName}`}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={progressBadgeText(goal)}
              variant={goal.progress.reached ? 'filled' : 'outlined'}
              sx={{ flexShrink: 0 }}
            />
          </Stack>

          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Amount
              cents={goal.savedCents as Cents}
              currency={goal.currency}
              colored={false}
              variant="h6"
              sx={{ fontWeight: 700 }}
            />
            <Typography variant="body2" color="text.secondary">
              / {formatMoneyText(goal.targetCents as Cents, goal.currency)}
            </Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            // 🔴 条长只影响"看起来多少"，数字仍显示真实百分比（超额 = 130.0%）
            value={barPercentOf(goal.progress.progressE4)}
            sx={{ height: 8, borderRadius: 1 }}
            data-testid={`goal-bar-${goal.id}`}
          />

          {/* 里程碑轨道：🔴 是否点亮由服务端下发的 `reached` 决定（取出后不熄灭） */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {goal.progress.milestones.map((m) => (
              <Chip
                key={m.tier}
                size="small"
                label={milestoneText(m.tier)}
                variant={m.reached ? 'filled' : 'outlined'}
                aria-label={milestoneAriaLabel(m.tier, m.reached)}
                sx={{
                  opacity: m.reached ? 1 : 0.45,
                  bgcolor: m.reached ? accent : 'transparent',
                  // 🔴 文字颜色由 MUI 按底色算对比色（不写字面量色值）
                  color: m.reached ? theme.palette.getContrastText(accent) : 'inherit',
                }}
              />
            ))}
            <Typography variant="caption" color="text.secondary">
              {remainingText(goal)}
            </Typography>
          </Stack>

          {goal.pace !== null && (
            <Typography variant="caption" sx={{ color: hintColor }} data-testid={`goal-pace-${goal.id}`}>
              {goal.pace.hint}
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary" data-testid={`goal-contrib-${goal.id}`}>
            {contributionsText(goal.contributions, goal.currency, meId)}
          </Typography>

          <Divider />

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={onSave}
              disabled={disabled}
              data-testid={`goal-save-${goal.id}`}
            >
              攒一笔
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={onWithdraw}
              disabled={disabled || (goal.savedCents as number) === 0}
              // 🔴 取出用琥珀（`AC-I2.4`）—— 绝不用红色（红 = 正/流入，铁律 8）
              sx={{ color: theme.jiaying.tokens['c-notice'], borderColor: theme.jiaying.tokens['c-notice'] }}
              data-testid={`goal-withdraw-${goal.id}`}
            >
              取出
            </Button>
            <Button size="small" onClick={onDetail} disabled={disabled} data-testid={`goal-detail-${goal.id}`}>
              明细
            </Button>
            <Button size="small" onClick={onEdit} disabled={disabled} data-testid={`goal-edit-${goal.id}`}>
              编辑
            </Button>
            <Button
              size="small"
              color="inherit"
              onClick={onDelete}
              disabled={disabled}
              data-testid={`goal-delete-${goal.id}`}
            >
              删除
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════ 贡献流抽屉 ═══════════════════════════

interface GoalContributionsDrawerProps {
  goalId: string | null;
  goal: GoalView | null;
  meId: string;
  onClose: () => void;
}

function GoalContributionsDrawer({
  goalId,
  goal,
  meId,
  onClose,
}: GoalContributionsDrawerProps): ReactElement {
  const q = useQuery({
    queryKey: ['goalContributions', goalId],
    queryFn: () => listGoalContributions(goalId as string),
    enabled: goalId !== null,
  });

  const body = (): ReactNode => {
    if (goalId === null) return null;
    if (q.isPending) return <Loading />;
    if (q.isError) return <Alert severity="error">明细读取失败：{String(q.error)}</Alert>;
    const data = q.data as GoalContributionListResult;
    return (
      <Stack spacing={1}>
        {goal !== null && goal.progress.reached && (
          <Card variant="outlined" sx={{ bgcolor: 'transparent' }} data-testid="goal-celebrate">
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                达成记录
              </Typography>
              {celebrateLines(goal, meId).map((line) => (
                <Typography key={line} variant="body2" color="text.secondary">
                  {line}
                </Typography>
              ))}
            </CardContent>
          </Card>
        )}
        {data.items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            还没有攒钱记录。
          </Typography>
        ) : (
          data.items.map((c) => (
            <Stack key={c.transactionId} direction="row" alignItems="center" spacing={1}>
              <Chip
                size="small"
                label={c.move === GoalMove.Save ? '攒入' : '取出'}
                variant={c.move === GoalMove.Save ? 'filled' : 'outlined'}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap>
                  {c.userName}
                  {c.note === null ? '' : ` · ${c.note}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.date}
                  {c.automatic ? ' · 自动' : ' · 手动'}
                </Typography>
              </Box>
              <Amount
                cents={c.amountCents as Cents}
                currency={data.currency}
                colored={false}
                variant="body2"
              />
            </Stack>
          ))
        )}
        {data.truncated && (
          <Typography variant="caption" color="text.secondary">
            记录较多，这里只显示最近 100 笔。
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <Drawer anchor="right" open={goalId !== null} onClose={onClose} data-testid="goal-contributions-drawer">
      <Stack sx={{ width: { xs: 320, sm: 420 }, p: 2 }} spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{goal?.name ?? '贡献流'}</Typography>
          <IconButton onClick={onClose} size="small" aria-label="关闭">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          谁攒的都算数 —— 不排名、不评分。
        </Typography>
        <Divider />
        {body()}
      </Stack>
    </Drawer>
  );
}
